import type { PlaceDetail } from "@/features/my/_types";
import {
  isOnboardingV7Version,
  isWidgetPhaseOnboardingStep,
} from "@/features/my/onboarding/constants/onboarding";

/**
 * The onboarding whose step the member redirects read. Only a v7 onboarding has one;
 * any other home needs no step before its pages can show.
 */
export function redirectOnboardingId(
  place: PlaceDetail | null | undefined,
): string | undefined {
  const membership = place?.latestMembership;
  if (!isOnboardingV7Version(Number(membership?.onboardingVersion))) {
    return undefined;
  }
  return membership?.onboardingId || undefined;
}

/**
 * Member place REST flags say onboarding is still running: active membership, an active
 * onboarding past version 1, and an `onboardingId` to load the step from.
 */
export function requiresOnboardingRedirectFromRest(
  place: PlaceDetail | null | undefined,
): boolean {
  if (!place) return false;
  const m = place.latestMembership;
  if (!m?.isActive || !m.onboardingActive) return false;
  const raw = m.onboardingVersion;
  const versionNum =
    raw === undefined || raw === null ? NaN : parseFloat(String(raw));
  if (Number.isNaN(versionNum) || !(versionNum > 1)) return false;
  return !!m.onboardingId;
}

/**
 * Never force `/my/onboarding` when the place has no **active** membership (unpaid /
 * not purchased). Owner Home used NeedsAttention* CTAs (checkout / contact) in that
 * state — onboarding only applies once membership is active (matches
 * `requiresOnboardingRedirectFromRest`, which already required `isActive`).
 */
export function shouldForceOnboardingRoute(
  place: PlaceDetail | null | undefined,
): boolean {
  if (!place?.latestMembership?.isActive) {
    return false;
  }
  return requiresOnboardingRedirectFromRest(place);
}

/**
 * The home must show its onboarding instead of the member's pages: a v7 onboarding is
 * running (see `shouldForceOnboardingRoute`) and its step is not a widget phase — those
 * phases run on the normal member pages.
 */
export function isOnboardingRequired(
  place: PlaceDetail | null | undefined,
  currentStep: string | null | undefined,
): boolean {
  return (
    shouldForceOnboardingRoute(place) &&
    !isWidgetPhaseOnboardingStep(currentStep) &&
    isOnboardingV7Version(Number(place?.latestMembership?.onboardingVersion))
  );
}
